/**
 * AiConfigEditor — Dialog for editing an AiConfig.
 * Fetches real model definitions from the API instead of hardcoding models.
 * Uses a single model selector grouped by provider.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Loader2 } from 'lucide-react';
import { useModels } from '@/lib/hooks/useModels';
import type { AiConfig } from '../types';

const TIER_BADGES: Record<string, { label: string; className: string }> = {
  fast: {
    label: 'Fast',
    className: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  },
  balanced: {
    label: 'Balanced',
    className: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  },
  powerful: {
    label: 'Powerful',
    className: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  },
};

interface AiConfigEditorProps {
  config: AiConfig | null;
  open: boolean;
  onClose: () => void;
  onSave: (config: AiConfig) => void;
}

export function AiConfigEditor({ config, open, onClose, onSave }: AiConfigEditorProps) {
  const { models, providers, loading } = useModels();
  const [label, setLabel] = useState('');
  const [modelDefinitionId, setModelDefinitionId] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [promptTemplate, setPromptTemplate] = useState('{{input}}');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2048);

  useEffect(() => {
    if (config) {
      setLabel(config.label);
      setModelDefinitionId(config.modelDefinitionId);
      setSystemPrompt(config.systemPrompt);
      setPromptTemplate(config.promptTemplate);
      setTemperature(config.parameters.temperature ?? 0.7);
      setMaxTokens(config.parameters.maxTokens ?? 2048);
    }
  }, [config]);

  if (!config) return null;

  function handleSave() {
    if (!config) return;
    onSave({
      configId: config.configId,
      label,
      modelDefinitionId,
      systemPrompt,
      promptTemplate,
      parameters: { temperature, maxTokens },
    });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit AI Config: {config.configId}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="config-label">Label</Label>
            <Input id="config-label" value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>

          <div>
            <Label>Model</Label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading models...
              </div>
            ) : (
              <Select value={modelDefinitionId} onValueChange={setModelDefinitionId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((provider) => {
                    const providerModels = models.filter((m) => m.provider === provider.key);
                    if (providerModels.length === 0) return null;
                    return (
                      <SelectGroup key={provider.key}>
                        <SelectLabel>{provider.displayName}</SelectLabel>
                        {providerModels.map((m) => {
                          const tier = TIER_BADGES[m.tier];
                          return (
                            <SelectItem key={m.entityId} value={m.entityId}>
                              <span className="flex items-center gap-2">
                                {m.name}
                                {tier && (
                                  <span
                                    className={`text-[10px] px-1 py-0.5 rounded ${tier.className}`}
                                  >
                                    {tier.label}
                                  </span>
                                )}
                                {m.isRecommended && (
                                  <span className="text-[10px] text-yellow-600 dark:text-yellow-400">
                                    Recommended
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <Label htmlFor="system-prompt">System Prompt</Label>
            <Textarea
              id="system-prompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={4}
              className="font-mono text-xs"
            />
          </div>

          <div>
            <Label htmlFor="prompt-template">
              Prompt Template
              <span className="text-muted-foreground text-xs ml-1">
                ({'{{input}}'} = user input)
              </span>
            </Label>
            <Textarea
              id="prompt-template"
              value={promptTemplate}
              onChange={(e) => setPromptTemplate(e.target.value)}
              rows={3}
              className="font-mono text-xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Temperature: {temperature.toFixed(2)}</Label>
              <Slider
                value={[temperature]}
                onValueChange={([v]) => setTemperature(v)}
                min={0}
                max={1}
                step={0.05}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="max-tokens">Max Tokens</Label>
              <Input
                id="max-tokens"
                type="number"
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value) || 2048)}
                min={100}
                max={16384}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!modelDefinitionId}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
